import { apiClient } from '../api-client';
import { ApiResponse } from '../../types/api';
import { DemographicsData, DemographicsFormData } from '../../types/patientmr/demographics';
import type { AddressVerification } from '../../dtos/patientmr/demographics.dto';

/** PUT …/demo response: the saved record, plus the USPS check result when the flag is on (PAT-01). */
export type DemographicsSaveResult = DemographicsData & { addressVerification?: AddressVerification };

/**
 * Array fields where an empty list is meaningful and must reach the server (clearing the last
 * hospitalization, switching allergies to "none known", unticking all DME). cleanPayload would
 * otherwise drop them.
 */
const PRESERVE_EMPTY_ARRAY_KEYS = ['pastHospitalizations', 'allergies', 'dmeEquipment'] as const;

/**
 * Recursively remove keys with empty string, null, undefined, empty array, or empty object values
 */
function cleanPayload<T>(obj: T): T {
  if (Array.isArray(obj)) {
    // Clean each item and filter out empty items
    return obj
      .map(cleanPayload)
      .filter(
        (item) =>
          item !== undefined &&
          item !== null &&
          !(typeof item === 'string' && item.trim() === '') &&
          !(Array.isArray(item) && item.length === 0) &&
          !(typeof item === 'object' && item && Object.keys(item).length === 0)
      ) as unknown as T;
  } else if (typeof obj === 'object' && obj !== null) {
    const cleaned: any = {};
    Object.entries(obj).forEach(([key, value]) => {
      const cleanedValue = cleanPayload(value);
      if (
        cleanedValue !== undefined &&
        cleanedValue !== null &&
        !(typeof cleanedValue === 'string' && cleanedValue.trim() === '') &&
        !(Array.isArray(cleanedValue) && cleanedValue.length === 0) &&
        !(typeof cleanedValue === 'object' && cleanedValue && Object.keys(cleanedValue).length === 0)
      ) {
        cleaned[key] = cleanedValue;
      }
    });
    return cleaned;
  }
  return obj;
}

/**
 * Fetch demographics for a specific patient
 */
export const fetchDemographics = async (patientId: string): Promise<DemographicsData> => {
  try {
    const response: ApiResponse<DemographicsData> = await apiClient.get(`/patientmr/${patientId}/demo`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to fetch demographics');
  } catch (error) {
    console.error(`Error fetching demographics for patient ${patientId}:`, error);
    throw error;
  }
};

/**
 * Create demographics for a patient
 */
export const createDemographics = async (patientId: string, demographicsData: DemographicsFormData): Promise<DemographicsData> => {
  try {
    // Map frontend model to API model based on the new structure, omitting backend-managed fields
    const {
      PK, SK, GSI1PK, GSI1SK, entityType, patientId: _pid, tenantId, consentInfo, accessLog, dataRetention, createdAt, updatedAt, createdBy, updatedBy,
      ...rest
    } = demographicsData;

    let payload = {
      ...rest
    };
    payload = cleanPayload(payload);

    const response: ApiResponse<DemographicsData> = await apiClient.post(`/patientmr/${patientId}/demo`, payload);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to create demographics');
  } catch (error) {
    console.error('Error creating demographics:', error);
    throw error;
  }
};

/**
 * Update demographics for a patient
 */
export const updateDemographics = async (patientId: string, demographicsData: DemographicsFormData): Promise<DemographicsSaveResult> => {
  try {
    // Send the complete demographics data to the backend, omitting backend-managed fields
    const {
      PK, SK, GSI1PK, GSI1SK, entityType, patientId: _pid, tenantId, consentInfo, accessLog, dataRetention, createdAt, updatedAt, createdBy, updatedBy,
      ...rest
    } = demographicsData;

    let payload = {
      ...rest
    };
    payload = cleanPayload(payload);
    const payloadRecord = payload as Record<string, unknown>;
    const restRecord = rest as Record<string, unknown>;
    for (const key of PRESERVE_EMPTY_ARRAY_KEYS) {
      if (Array.isArray(restRecord[key]) && !(key in payloadRecord)) {
        payloadRecord[key] = [];
      }
    }

    const response: ApiResponse<DemographicsSaveResult> = await apiClient.put(`/patientmr/${patientId}/demo`, payload);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to update demographics');
  } catch (error) {
    console.error('Error updating demographics:', error);
    throw error;
  }
};

/**
 * Delete demographics for a patient
 */
export const deleteDemographics = async (patientId: string): Promise<boolean> => {
  try {
    const response: ApiResponse<void> = await apiClient.delete(`/patientmr/demographics/${patientId}`);

    if (response.success) {
      return true;
    }

    throw new Error(response.error?.message || 'Failed to delete demographics');
  } catch (error) {
    console.error(`Error deleting demographics for patient ${patientId}:`, error);
    throw error;
  }
};

/**
 * Response interface for search demographics
 */
interface SearchDemographicsResponse {
  demographics: DemographicsData[];
  totalCount: number;
  pagination: any;
}

/**
 * Search demographics with optional filtering
 */
export const searchDemographics = async (
  search?: string,
  page: number = 1,
  limit: number = 100
): Promise<{ demographics: DemographicsData[], totalCount: number, pagination: any }> => {
  try {
    // Build query parameters
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };

    if (search) {
      params.search = search;
    }

    const response: ApiResponse<SearchDemographicsResponse> = await apiClient.get('/patientmr/demographics', params);

    if (response.success && response.data) {
      // Handle different API response formats and provide defaults
      const apiDemographics = response.data.demographics || [];

      // Extract pagination data if available
      const pagination = response.data.pagination || response.pagination || {
        page,
        limit,
        totalCount: response.data.totalCount || apiDemographics.length,
        totalPages: Math.ceil((response.data.totalCount || apiDemographics.length) / limit),
        hasNext: ((page * limit) < (response.data.totalCount || apiDemographics.length)),
        hasPrev: page > 1
      };

      return {
        demographics: apiDemographics,
        totalCount: response.data.totalCount || apiDemographics.length,
        pagination
      };
    }

    throw new Error(response.error?.message || 'Failed to search demographics');
  } catch (error) {
    console.error('Error searching demographics:', error);
    throw error;
  }
};

/**
 * Validate demographics data before submission
 */
export const validateDemographics = (data: DemographicsFormData): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Required field validations
  if (!data.firstName?.trim()) {
    errors.push('First name is required');
  }
  if (!data.lastName?.trim()) {
    errors.push('Last name is required');
  }
  if (!data.dateOfBirth) {
    errors.push('Date of birth is required');
  }
  if (!data.gender) {
    errors.push('Gender is required');
  }
  if (!data.phoneNumber?.trim()) {
    errors.push('Phone number is required');
  }

  // Email validation
  if (data.email && !isValidEmail(data.email)) {
    errors.push('Invalid email format');
  }

  // Phone number validation
  if (data.phoneNumber && !isValidPhoneNumber(data.phoneNumber)) {
    errors.push('Invalid phone number format');
  }

  // Address validation
  if (data.address) {
    if (!data.address.street1?.trim()) {
      errors.push('Street address is required');
    }
    if (!data.address.city?.trim()) {
      errors.push('City is required');
    }
    if (!data.address.state?.trim()) {
      errors.push('State is required');
    }
    if (!data.address.zipCode?.trim()) {
      errors.push('Zip code is required');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Helper function to validate email format
 */
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Helper function to validate phone number format
 */
const isValidPhoneNumber = (phone: string): boolean => {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  // Check if it's a valid US phone number (10 digits)
  return cleaned.length === 10;
};

/**
 * Format phone number for display
 */
export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

/**
 * Format date for display
 */
export const formatDate = (date: string): string => {
  try {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return date;
  }
};
